'use client'

import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Spinner, useBreakpointValue, useDisclosure, useStyleConfig} from '@chakra-ui/react'
import CountrySelectorDrawer from '../country-selector-drawer'
import CountrySelectorDropdown from '../country-selector-dropdown'
import CountrySelectorConfirmationDrawer from '../country-selector-confirmation-drawer'
import {useCountrySelector, sortCountries} from '../../hooks/use-country-selector'
import {useLocation} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useHeaderContext} from '../../contexts/header-context'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import { noop } from '@salesforce/retail-react-app/app/utils/utils'
import {isLocalhost} from '../../utils/utils'
import {useBrand} from '../../contexts/brand-context'

const LOCALHOST_COUNTRY_URLS = {
    fr_CA: 'http://localhost:3000/fr-ca/',
    en_CA: 'http://localhost:3000/en-ca/',
    en_US: 'http://localhost:3000/us',
    en_GB: 'http://localhost:3000/uk'
}

const CountrySelector = ({
    triggerRef = {},
    onShowCountrySelectorOnFooter = noop,
    variant = 'header',
}) => {
    const location = useLocation()
    const {category, product, pageType} = useHeaderContext()
    const {brandId} = useBrand()
    const {locale: originalLocale} = useMultiSite()
    const {data: basket} = useCurrentBasket()
    const deleteBasket = useShopperBasketsMutation('deleteBasket')
    const isMobile = useBreakpointValue({base: true, md: false})
    const {isOpen, onOpen, onClose} = useDisclosure()
    const styles = useStyleConfig('CountrySelector', {variant})
    const internalRef = React.useRef()
    // Use triggerRef from props if provided, otherwise use internal ref
    const drawerTriggerRef = triggerRef || internalRef

    const currentLanguage = originalLocale?.id?.replace('-', '_') || 'en_GB'
    const [currentLanguageCode, currentCountryPart] = currentLanguage.split('_')
    const extractedCountryCode = currentCountryPart || 'GB'
    const baseCountryCode = extractedCountryCode === 'GB' ? 'UK' : extractedCountryCode
    const currentCountryCode =
        currentLanguageCode !== 'en'
            ? `${currentLanguageCode.toUpperCase()}-${baseCountryCode}`
            : baseCountryCode
    const pathname = location.pathname.startsWith('/')
        ? location.pathname.substring(1)
        : location.pathname

    const [countries, setCountries] = useState(null)
    const [pendingLanguage, setPendingLanguage] = useState(null)
    const [pendingCountryCode, setPendingCountryCode] = useState(null)

    const isProductOrCategoryPage =
        pageType === 'product-detail' || pageType === 'product-list'

    const {countrySelectorData, isLoading: isCountriesDataLoading} = useCountrySelector()

    const transformCountryConfig = (countries) => {
        return countries?.reduce((acc, country) => {
            acc[country.locale] = {
                label: country.label,
                locale: country.locale,
                url: country.url,
                isProductOrCategoryPage: true
            }

            return acc
        }, {})
    }

    // If the brandId is 'dynamite', removes the 'en_GB' property from the countries object.
    const filterCountriesByBrand = (countriesObj) => {
        if (!countriesObj || brandId !== 'dynamite') return countriesObj
        const {en_GB, ...rest} = countriesObj
        return rest
    }

    useEffect(() => {
        if (isProductOrCategoryPage) {
            const transformedCountries =
                pageType === 'product-list'
                    ? transformCountryConfig(category?.c_countrySelectorURLs)
                    : transformCountryConfig(product?.c_countrySelectorURLs) ||
                      transformCountryConfig(category?.c_countrySelectorURLs)
            const hasProductOrCategoryCountries =
                transformedCountries && Object.keys(transformedCountries).length > 0

            setCountries(
                filterCountriesByBrand(
                    hasProductOrCategoryCountries
                        ? sortCountries(transformedCountries)
                        : countrySelectorData?.countrySelectorConfig
                )
            )
        } else {
            setCountries(filterCountriesByBrand(countrySelectorData?.countrySelectorConfig))
        }

        onShowCountrySelectorOnFooter(countrySelectorData?.showCountrySelectorOnFooter)
    }, [countrySelectorData, location?.pathname, category, product, isProductOrCategoryPage, brandId])

    const handleLanguageChange = (selectedLanguage, forceRedirect = false) => {
        const selectedConfig = countrySelectorData?.countrySelectorConfig[selectedLanguage]
        const exceptionConfig = countries[selectedLanguage]
        let fullUrl = null
        const isLocalEnv = isLocalhost(window.location.hostname)

        if (isLocalEnv && LOCALHOST_COUNTRY_URLS[selectedLanguage]) {
            fullUrl = LOCALHOST_COUNTRY_URLS[selectedLanguage]
        } else if (exceptionConfig?.isProductOrCategoryPage) {
            fullUrl = exceptionConfig?.url
        } else {
            const domainValue = selectedConfig?.domain
            const domain =
                (isLocalEnv
                    ? window.location.origin
                    : domainValue && typeof domainValue === 'object'
                      ? domainValue[brandId]
                      : domainValue) || window.location.origin
            const path =
                countrySelectorData?.countrySelectorPages[pathname]?.[selectedLanguage] ??
                selectedConfig?.path ??
                pathname
            const fullDomain = domain.startsWith('http') ? domain : `https://${domain}`
            const cleanDomain = fullDomain.replace(/\/$/, '')
            const cleanPath = path.startsWith('/') ? path : `/${path}`
            fullUrl = `${cleanDomain}${cleanPath}`
        }

        const newCountryCode = selectedLanguage?.split('_')[1]
        const nextCountryCode = newCountryCode === 'GB' ? 'UK' : newCountryCode

        if (basket?.productItems?.length > 0 && !forceRedirect) {
            setPendingLanguage(selectedLanguage)
            setPendingCountryCode(nextCountryCode)
            onOpen()
        } else {
            // Close the CountrySelectorDrawer for mobile before redirecting
            if (isMobile && drawerTriggerRef.current?.onClose) {
                drawerTriggerRef.current.onClose()
            }
            window.location.href = fullUrl
        }
    }

    const handleOnConfirmation = (confirmation) => {
        switch (confirmation) {
            case 'yes':
                if (basket && basket?.basketId) {
                    deleteBasket.mutate(
                        {
                            parameters: {basketId: basket.basketId}
                        },
                        {
                            onSuccess: () => {
                                handleLanguageChange(pendingLanguage, true)
                            }
                        }
                    )
                }
                break
            case 'no':
                setPendingCountryCode(null)
                setPendingLanguage(null)
                break
            default:
                break
        }
    }

    const handleConfirmationDrawerClose = () => {
        setPendingCountryCode(null)
        setPendingLanguage(null)
        onClose()
        // Close the CountrySelectorDrawer if it's open
        if (isMobile && drawerTriggerRef.current?.onClose) {
            drawerTriggerRef.current.onClose()
        }
    }

    if (isCountriesDataLoading && !countries) {
        return (
            <Box {...styles.spinnerContainer}>
                <Spinner {...styles.spinner} />
            </Box>
        )
    }

    return (
        <>
            <Box {...styles.container}>
                {isMobile ? (
                    <CountrySelectorDrawer
                        variant={variant}
                        countries={countries}
                        language={currentLanguage}
                        countryCode={currentCountryCode}
                        handleLanguageChange={handleLanguageChange}
                        triggerRef={drawerTriggerRef}
                    />
                ) : (
                    <CountrySelectorDropdown
                        variant={variant}
                        countries={countries}
                        language={currentLanguage}
                        countryCode={currentCountryCode}
                        handleLanguageChange={handleLanguageChange}
                    />
                )}
            </Box>
            <CountrySelectorConfirmationDrawer
                isOpen={isOpen}
                onClose={handleConfirmationDrawerClose}
                fromCountry={currentCountryCode}
                toCountry={pendingCountryCode || currentCountryCode}
                onConfirmation={handleOnConfirmation}
            />
        </>
    )
}

CountrySelector.propTypes = {
    triggerRef: PropTypes.object,
    onShowCountrySelectorOnFooter: PropTypes.func,
    variant: PropTypes.string
}

export default CountrySelector
